import { EmailEngineSevice } from "../../../../../server/api/email-engine-v2";
import { authHeader } from "../../../../../utils/auth-header";
import { serviceURL } from "../../../../../utils/service-url";
import { uuidToShortId } from "../../../../../utils/uuid-to-short-id";
import { SupabaseEmailDesignService } from "../../../../email-design/server/supabase-email-design-service";
import { getEmailPreviewSource } from "../../../../moment/component/buttons/preview-button/preview-dialog/hooks/use-email-preview-source";
import { SupabaseMomentService } from "../../../../moment/server/supabase-moment-service";
import { SupabaseMomentPreviewService } from "../../../../moment/server/supabase-preview-service";
import { showroomClient } from "../../../../showroom/client";
import { calculatePaperSize } from "../../utils/convert-to-mjml/calculate-paper-size";
import { $convertToMjmlString } from "../../utils/convert-to-mjml/convert-to-mjml";
import { createConfiguredEditor } from "../../utils/headless-editor";

import type { DatabaseWithOptions } from "../../../../../__definitions__/database-definitions";
import type { UUID } from "../../../../../utils/uuid";
import type { PreviewMomentData } from "../../../../moment/component/buttons/preview-button/preview-dialog/types";
import type { PostgrestClient } from "@supabase/postgrest-js";

// Loads preview merge fields for a moment using the same logic as `usePreviewTags`,
// but without depending on form context. Falls back to empty object
// since there is no specific recipient/subject in this context.
async function getPreviewMergeFields(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"]>,
  previewSource: ReturnType<typeof getEmailPreviewSource>,
): Promise<Record<string, string>> {
  const { data: previewTestData } = await new SupabaseMomentPreviewService(
    supabase,
  ).getMomentPreviewTestData(
    previewSource.design,
    previewSource.subject,
    previewSource.additional_merge_fields,
  );

  if (!previewTestData) return {};

  return Object.fromEntries(previewTestData.map((field) => [field.key, field.merge_sample ?? ""]));
}

function toEmailPreviewSourceInput(
  data: PreviewMomentData,
): Parameters<typeof getEmailPreviewSource>[0] {
  return {
    channel: data.channel,
    use_lexical_editor: data.use_lexical_editor ?? false,
    design: data.design,
    email_subject: data.email_subject,
    from: data.from,
    email_moment: data.email_moment
      ? {
          subject: data.email_moment.subject ?? "",
          from_address: data.email_moment.from_address ?? "",
          body: data.email_moment.body ?? "",
        }
      : null,
  };
}

// Handles both lexical (MJML) and newsletter (HTML) moments.
export async function publishMomentToShowroom(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"]>,
  accountId: UUID,
  momentId: UUID,
  token: string,
): Promise<string | null> {
  const { data: momentData, error: rpcError } = await supabase
    .rpc("moment_preview_data", { moment_id_: momentId, account_id_: accountId })
    .single();

  if (rpcError || !momentData) return null;

  const previewSource = getEmailPreviewSource(toEmailPreviewSourceInput(momentData));

  let previewHtml: string;
  const service = new EmailEngineSevice(token);

  if (momentData.use_lexical_editor) {
    // Lexical path: build MJML -> render via email engine
    const [{ data: emailDesign }, mailMergeFields] = await Promise.all([
      new SupabaseEmailDesignService(supabase).maybeGet(
        momentData.email_design_id ?? momentData.account_id,
      ),
      getPreviewMergeFields(supabase, previewSource),
    ]);

    const editor = createConfiguredEditor({ format: "email" });
    if (momentData.design && momentData.design.type !== "markdown") {
      editor.setEditorState(editor.parseEditorState(momentData.design));
    }

    const mjmlBody = editor
      .getEditorState()
      .read(() =>
        $convertToMjmlString(
          editor,
          momentData.account_id,
          "paper",
          calculatePaperSize(emailDesign?.layout_settings),
          {},
          emailDesign ?? undefined,
        ),
      );

    const fullMjml = `<mjml>${emailDesign?.styles_mjml ?? ""}
      <mj-body>
      ${emailDesign?.header_mjml ?? ""}
      ${mjmlBody}
      ${emailDesign?.footer_mjml ?? ""}
      </mj-body>
      </mjml>`;

    const response = await service.buildMessage({
      body: fullMjml,
      mail_merge_fields: mailMergeFields,
      dynamic_condition_fields: {},
      mode: "mjml",
    });

    if (response.error || !response.data) return null;
    previewHtml = response.data.data;
  } else {
    // Newsletter path: use email_moment body -> render via email engine
    if (!momentData.email_moment) return null;

    const mailMergeFields = await getPreviewMergeFields(supabase, previewSource);

    const response = await service.buildMessage({
      body: momentData.email_moment.body ?? "",
      mail_merge_fields: mailMergeFields,
      dynamic_condition_fields: {},
      mode: "html",
    });

    if (response.error || !response.data) return null;
    previewHtml = response.data.data;
  }

  const shortId = await uuidToShortId(momentId);

  const showRoomResult = await showroomClient.POST("/publish", {
    body: { short_id: shortId, preview_html: previewHtml, subject: previewSource.subject ?? "" },
    ...authHeader(token),
  });

  if (showRoomResult.error !== undefined) return null;
  const published = showRoomResult.data;
  if (!published) return null;
  const publishedKey = published.key ?? shortId;

  const { error: updateError } = await new SupabaseMomentService(supabase).update(momentId, {
    showroom_key: publishedKey,
  });

  if (updateError) return null;

  return published.url ?? `${serviceURL("showroom")}${publishedKey}`;
}
